/* eslint-disable @typescript-eslint/no-use-before-define */
import type React from 'react';

import * as styles from './styles';
import { Button, Typography } from '@cobliteam/ui/components';
import { Table, TableBody } from '@material-ui/core';

import { Location, type UUID, type Vehicle } from '@cobliteam/common-types';

import { isVehicleDashcam } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';
import { CameraIdentifier } from '@cobliteam/media';

import { Checkbox, DescriptionListDefault, EditIcon, HintPopup, type ITableColumn, PermissionsGate, TableCell, TableHead } from '@cobliteam/base-components';

import MaintenancePermissions from '../../../../permissions';

export interface VehicleOdometer {
  [vehicleId: UUID]: number | undefined;
}

export interface VehicleSelectionProps {
  vehiclesWithoutReminder: Vehicle[];
  vehiclesCurrentOdometer: VehicleOdometer;
  onChange: (data: VehicleOdometer) => void;
}

export const VehicleSelection: React.FC<VehicleSelectionProps> = ({ vehiclesWithoutReminder, vehiclesCurrentOdometer, onChange }) => {
  const getDeviceIcon = (row: Vehicle) => {
    return isVehicleDashcam(row) ? <CameraIdentifier hasCta={false} /> : <></>;
  };

  const columns: Array<ITableColumn<Vehicle>> = [
    {
      id: 'iconsColumn',
      label: '',
      sortable: false,
      Cell: (row) => getDeviceIcon(row),
      className: styles.iconsColumn,
    },
    {
      id: 'vehicleName',
      label: 'Selecionar todos',
      sortable: false,
      Cell: (row) => row.label,
    },
  ];

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    Analytics.track('Maintenance Reminders Setup All All Rows Changed', {
      checked: checked ? 'check' : 'uncheck',
    });

    if (checked) {
      const odometers: VehicleOdometer = {};

      vehiclesWithoutReminder.forEach((vehicle) => {
        if (hasVehicleOdometer(vehicle)) {
          odometers[vehicle.id] = vehicle.odometer?.odometerInKm;
        }
      });

      onChange(odometers);
    } else {
      onChange({});
    }
  };

  const handleRowSelect = (row: Vehicle) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    Analytics.track('Maintenance Reminders Setup Row Clicked', {
      checked: checked ? 'check' : 'uncheck',
    });

    if (checked) {
      onChange({
        ...vehiclesCurrentOdometer,
        [row.id]: row.odometer?.odometerInKm,
      });
    } else {
      const { [row.id]: _, ...otherOdometers } = vehiclesCurrentOdometer;
      onChange(otherOdometers);
    }
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    Analytics.track('Opened Vehicle Edit From Setup Reminder Modal');
    const url = `${window.location.origin}/#${Location.VEHICLES}/${vehicle.id}`;
    window.open(url, '_blank')!.focus();
  };

  const isSelected = (row: Vehicle) => Object.keys(vehiclesCurrentOdometer).includes(row.id);

  const checkOdometer = (val: any) => val && val > 0;

  const hasVehicleOdometer = (row: Vehicle) => {
    return checkOdometer(row.odometer?.odometerInKm);
  };

  const getOdometerValue = ({ odometer }: Vehicle) => {
    return checkOdometer(odometer?.odometerInKm) ? `${odometer?.odometerInKm} km` : 'Sem dados';
  };

  return (
    <Table className={styles.tableStyle} aria-labelledby='Veículos'>
      <TableHead
        orderBy={columns[1]}
        order='asc'
        onSelectAllClick={handleSelectAllClick}
        numSelected={Object.keys(vehiclesCurrentOdometer).length}
        rowCount={vehiclesWithoutReminder.length}
        columns={columns}
        selectionConfig={{
          selectable: true,
          className: styles.checkboxCell,
        }}
      />
      <TableBody>
        {vehiclesWithoutReminder.map((row) => (
          <tr key={row.id}>
            <TableCell>
              <div className={styles.outterCheckboxWrapper}>
                <Checkbox disabled={!hasVehicleOdometer(row)} checked={isSelected(row)} onChange={handleRowSelect(row)} />
              </div>
            </TableCell>
            <TableCell className={styles.iconsColumnData}>{getDeviceIcon(row)}</TableCell>
            <td className={styles.vehicleColumn}>
              <HintPopup title='Para programar um lembrete, é necessário cadastrar o valor de odômetro do veículo' placement='bottom' enabled={!hasVehicleOdometer(row)} arrow>
                <Typography variant='input-placeholder' className={styles.description(checkOdometer(row.odometer?.odometerInKm))}>
                  {row.label}
                </Typography>
              </HintPopup>
            </td>
            <TableCell>
              <div className={styles.row(checkOdometer(row.odometer?.odometerInKm))}>
                <DescriptionListDefault>Odômetro atual:</DescriptionListDefault>
                <DescriptionListDefault>{getOdometerValue(row)}</DescriptionListDefault>
              </div>
            </TableCell>
            <PermissionsGate anyOfPermissions={MaintenancePermissions.CREATE_UPDATE_DELETE_VEHICLE}>
              <TableCell>
                <Button id={row.id} variant='text' startIcon={<EditIcon />} onClick={() => handleEditVehicle(row)}>
                  Editar veículo
                </Button>
              </TableCell>
            </PermissionsGate>
          </tr>
        ))}
      </TableBody>
    </Table>
  );
};
